import React, { useCallback } from 'react';
import { getMonthList, getYearsList, nullFn } from '../../utils';
import { CalendarHeaderProps } from '../../types';
import RightChevron from '../../../../../assets/images/svg/RightChevron';
import LeftChevron from '../../../../../assets/images/svg/LeftChevron';
import useHeaderArrowNav from '../../hooks/useHeaderArrowNav';
import CalendarHeaderYearSelector from './CalendarHeaderYearSelector';
import CalendarHeaderMonthSelector from './CalendarHeaderMonthSelector';

export default function CalendarHeader({
  currentYear = undefined,
  formattedMonth = undefined,
  disableNextMonth = false,
  disablePrevMonth = false,
  locale = 'en-US',
  onYearChange = nullFn,
  onMonthChange = nullFn,
  onNextMonthClick = nullFn,
  onPrevMonthClick = nullFn,
}: CalendarHeaderProps): JSX.Element {
  const refs = [
    React.useRef<HTMLButtonElement>(null),
    React.useRef<HTMLButtonElement>(null),
  ];

  const monthsList = getMonthList(currentYear, 'long', locale);
  const yearsList = getYearsList(currentYear);

  const {
    updateFocusedItem,
    clearFocusedItems,
    handleKeyDown,
  } = useHeaderArrowNav({
    disableNextMonth,
    disablePrevMonth,
    refs,
    firstIdx: disablePrevMonth ? 1 : 0,
    lastIdx: 2,
    delta: disableNextMonth ? 2 : 1,
  });

  const handleOnPrevMonthClick = useCallback(() => {
    onPrevMonthClick();
    updateFocusedItem(0);
  }, [onPrevMonthClick, updateFocusedItem]);

  const handleOnNextMonthClick = useCallback(() => {
    onNextMonthClick();
    updateFocusedItem(1);
  }, [onNextMonthClick, updateFocusedItem]);

  const nextMonthTabIdx = disablePrevMonth ? undefined : -1;

  const handleOnMonthClick = useCallback(
    (month: number) => {
      onMonthChange(month);
    },
    [onMonthChange],
  );

  const handleOnYearClick = useCallback(
    (year: number) => {
      onYearChange(year);
    },
    [onYearChange],
  );

  return (
    // eslint-disable-next-line jsx-a11y/interactive-supports-focus
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className='flex justify-between items-center flex-grow py-5 mx-auto'
      onKeyDown={handleKeyDown}
    >
      <button
        type='button'
        className='w-1/7 flex items-center justify-center outline-none focus:outline-none'
        onClick={handleOnPrevMonthClick}
        ref={refs[0]}
        onFocus={() => updateFocusedItem(0)}
        disabled={disablePrevMonth}
        onBlur={clearFocusedItems}
      >
        <LeftChevron />
      </button>
      <div className='flex flex-row flex-1 items-center justify-center space-x-2 select-none'>
        <CalendarHeaderMonthSelector
          formattedMonth={formattedMonth}
          monthList={monthsList}
          handleOnClick={handleOnMonthClick}
        />
        <CalendarHeaderYearSelector
          currentYear={currentYear}
          yearsList={yearsList}
          handleOnClick={handleOnYearClick}
        />
      </div>
      <button
        type='button'
        className='w-1/7 flex items-center justify-center outline-none focus:outline-none'
        tabIndex={nextMonthTabIdx}
        onClick={handleOnNextMonthClick}
        ref={refs[1]}
        onFocus={() => updateFocusedItem(1)}
        disabled={disableNextMonth}
        onBlur={clearFocusedItems}
      >
        <RightChevron />
      </button>
    </div>
  );
}
